'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { DeferredCardCartButton } from './deferred-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { AskForPriceButton } from '@eliadmin/core/src/components/product/ask-for-price-button';
import { ProductPrice } from '@eliadmin/core/src/components/product/product-price';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn, getContrastYIQ } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { ProductBadge } from '../detail/product-badge';
import { CatalogueLink as Link } from '@/components/catalogue-navigation';
import { ProductRating } from '@eliadmin/core/src/components/product/detail/product-rating';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';

export type ProductCardProps = {
  product: Product;
  className?: { main: string };
  border?: boolean;
};

function ProductCardInternal({ product, className, border = true }: ProductCardProps) {
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const filter = useCategoryStore(state => state.filter);

  const filterVariants = getProductFilterVariants(product, filter);
  const image = getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, []);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings.product_detail?.price_without_vat_first?.enabled,
  });
  const isDiscounted = priceDisplay.showPreviousPrice;

  const hasTags = product?.tags && product?.tags?.length > 0;

  const showBadges = isDiscounted || hasTags;

  return (
    <div
      className={cn(
        'relative col-span-1 flex h-full w-full min-w-0 flex-col rounded-[20px]',
        border && 'border border-border',
        className?.main,
      )}
    >
      <Link
        href={productUrl}
        prefetchOnHover
        aria-label={product.name}
        className="absolute inset-0 z-10 rounded-[20px]"
      />
      <div className="flex w-full min-w-0 flex-col items-start justify-start p-3 pb-0">
        <div className="flex h-[19px] w-full flex-nowrap items-start justify-start gap-1 overflow-hidden">
          {showBadges && (
            <>
              {product?.tags?.map(tag => (
                <ProductBadge
                  background={tag?.color ?? ''}
                  key={tag?.name}
                  color={getContrastYIQ(tag?.color ?? '')}
                  text={tag?.name}
                  className="shrink-0"
                />
              ))}
              {isDiscounted && (
                <ProductBadge
                  color="#FFFFFF"
                  background={'#C31E1E'}
                  text={`-${priceDisplay.discountLabel}%`}
                  className="shrink-0"
                />
              )}
            </>
          )}
        </div>
        <p className="mt-2 line-clamp-2 h-[34px] w-full text-sm font-bold leading-[120%] text-black lg:h-[39px] lg:text-base lg:leading-[120%]">
          {product.name}
        </p>
        <div className="mt-1 flex min-h-6 w-full items-center">
          <ProductPrice product={product} />
        </div>
        <div className="mt-1 h-6 w-full">
          {(product?.ratingCount ?? 0) > 0 && (
            <ProductRating rating={product.rating ?? 0} ratingCount={product.ratingCount ?? 0} />
          )}
        </div>
      </div>
      <div className="relative aspect-square w-full">
        <Image
          src={image?.url ?? '/images/placeholder.png'}
          alt={resolveImageAlt(image, product?.name)}
          fill
          sizes="(min-width: 1280px) 272px, (min-width: 768px) 220px, (min-width: 640px) 195px, 173px"
          className="absolute inset-0 z-0 h-full w-full object-contain object-center p-4 lg:p-6"
        />
      </div>
      <div className="mt-auto flex w-full items-center justify-between gap-2 p-3">
        {priceDisplay.priceOnRequest ? (
          <AskForPriceButton
            product={product}
            className={cn('relative z-20 h-12 flex-grow', 'ea-product-card__ask-for-price-button')}
          />
        ) : (
          <DeferredCardCartButton
            product={product}
            showIcon={!!templateSettings.product_teaser?.cart_icon}
            sellable={isSellable(product, 1)}
            quantity={1}
            customText={t('text1115')}
            className={cn('relative z-20 h-12 flex-grow', 'ea-product-card__add-to-cart-button')}
            inCard
          />
        )}
        <AddToFavoritesButton
          buttonClassName="relative z-20"
          slug={product.slug}
          buttonSize={'icon'}
          buttonVariant={'white'}
          productId={product.id}
          isFavorite={product.is_favorite}
          showText={false}
        />
      </div>
    </div>
  );
}

export function ProductCard(props: ProductCardProps) {
  return <ProductCardInternal {...props} />;
}

export function ProductCardSkeleton({
  amount,
  variant = 'vertical',
}: {
  amount: number;
  variant?: 'horizontal' | 'vertical';
}) {
  return (
    <>
      {Array.from({ length: amount }).map((_, i) =>
        variant === 'vertical' ? (
          <div
            key={i}
            aria-hidden="true"
            className="col-span-1 flex h-full w-full min-w-0 animate-pulse flex-col rounded-[20px] border border-border"
          >
            <div className="flex w-full min-w-0 flex-col items-start justify-start p-3 pb-0">
              <div className="h-[19px] w-1/2 rounded bg-neutral-100" />
              <div className="mt-2 h-[34px] w-full rounded bg-neutral-100 lg:h-[39px]" />
              <div className="mt-1 min-h-6 w-2/3 rounded bg-neutral-100" />
              <div className="mt-1 h-6 w-1/2 rounded bg-neutral-100" />
            </div>
            <div className="relative aspect-square w-full bg-neutral-50" />
            <div className="mt-auto flex w-full items-center justify-between gap-2 p-3">
              <div className="h-12 flex-grow rounded-[20px] bg-neutral-100" />
              <div className="h-12 w-12 shrink-0 rounded-[20px] bg-neutral-100" />
            </div>
          </div>
        ) : (
          <div
            key={i}
            aria-hidden="true"
            className={cn(
              'animate-pulse',
              'col-span-1 w-full',
              'flex items-center justify-start gap-3 pr-2 sm:gap-4 md:pr-3 lg:pr-4',
            )}
          >
            <div
              className={cn(
                'aspect-square w-full rounded-[20px] bg-neutral-50',
                'max-w-[160px] sm:max-w-[170px] lg:max-w-[190px]',
              )}
            />
            <div className={cn('h-[100px] w-full rounded-[20px] bg-neutral-50 sm:h-[113px]')} />
          </div>
        ),
      )}
    </>
  );
}
